import { createAsyncThunk } from '@reduxjs/toolkit';
import AsyncStorage from '~global/storage';
import api from '~api/affiliates';
import HelpApi from '~api/help';
import { Catalog } from '~api/catalog';

const AFFILIATE_STORAGE_KEY = 'affiliate_histories';
export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export const isDuplicateNikError = (payload = {}) => {
  const code = payload?.error_code || payload?.errorCode || payload?.code;
  return (
    code === 'IDENTITY_NUMBER_ALREADY_EXISTS' ||
    /identity_number|\bNIK\b/i.test(payload?.message || '')
  );
};

export const isSelfAttribution = (currentUserId, affiliateUserId) =>
  Boolean(
    currentUserId &&
      affiliateUserId &&
      String(currentUserId) === String(affiliateUserId),
  );

const readAttributionMap = async () => {
  try {
    const raw = await AsyncStorage.getItem(AFFILIATE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const writeAttributionMap = async (map) => {
  try {
    await AsyncStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(map));
  } catch (e) {}
};

const pruneExpired = (map) => {
  const now = Date.now();
  let changed = false;
  Object.keys(map).forEach((key) => {
    if (now - (map[key]?.click_date || 0) >= SEVEN_DAYS_MS) {
      delete map[key];
      changed = true;
    }
  });
  return changed;
};

// Account-level status — dispatched from several surfaces (Account card, PDP
// banner, PLP pill). The condition dedupes concurrent fetches; pass
// `{ force: true }` after mutations (register / KTP update) to always refresh.
export const fetchStatus = createAsyncThunk(
  'affiliate/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getAffiliateInfo();
      return response.data?.data ?? null;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    condition: (arg, { getState }) =>
      arg?.force === true || getState().affiliate.status.fetch !== 'pending',
  },
);

export const register = createAsyncThunk(
  'affiliate/register',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.registerAffiliate(payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const fetchCommission = createAsyncThunk(
  'affiliate/fetchCommission',
  async ({ id, combinationId, qty = 1 }, { rejectWithValue }) => {
    try {
      const item = { id, qty };
      if (combinationId) {
        item.combination_id = combinationId;
      }
      const response = await api.getProductCommission([item]);
      const list = response.data?.data ?? response.data ?? [];
      const data = Array.isArray(list)
        ? list.find((entry) => String(entry.id) === String(id)) || list[0]
        : list;
      return { id, data: data || null };
    } catch (error) {
      return rejectWithValue({
        id,
        error: error.response?.data || error.message,
      });
    }
  },
);

export const fetchPromotedProduct = createAsyncThunk(
  'affiliate/fetchPromotedProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const getRes = await api.getUserPromotedProducts({
        'filter[product.id]': productId,
      });
      const list = getRes.data?.data;
      if (Array.isArray(list) && list.length > 0) {
        return {
          productId,
          affiliates_product_id: list[0].affiliates_product_id,
        };
      }
    } catch (_) {}

    try {
      const postRes = await api.submitPromotedProduct(productId);
      const data = postRes.data?.data ?? postRes.data;
      return { productId, affiliates_product_id: data?.affiliates_product_id };
    } catch (error) {
      const code =
        error.response?.data?.error_code ||
        error.response?.data?.errorCode ||
        error.response?.data?.code;

      if (code === 'PRODUCT_ALREADY_PROMOTED') {
        try {
          const retry = await api.getUserPromotedProducts({
            'filter[product.id]': productId,
          });
          const list = retry.data?.data;
          if (Array.isArray(list) && list.length > 0) {
            return {
              productId,
              affiliates_product_id: list[0].affiliates_product_id,
            };
          }
        } catch (_) {}
      }

      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const recordAttribution = createAsyncThunk(
  'affiliate/recordAttribution',
  async (
    { productId, affiliateProductId, affiliateUserId, clickDate },
    { getState },
  ) => {
    const pid = String(productId || '');
    const apid = Number(affiliateProductId) || 0;
    if (!pid || !apid) {
      return null; // nothing to attribute
    }

    // Self-benefit guard: the link owner is never attributed to their own link.
    const user = getState().auth?.user || {};
    if (isSelfAttribution(user.id || user._id, affiliateUserId)) {
      return null;
    }

    const map = await readAttributionMap();
    pruneExpired(map);
    map[pid] = {
      product_id: pid,
      affiliate_product_id: apid,
      affiliate_user_id: affiliateUserId ? String(affiliateUserId) : null,
      click_date: clickDate || Date.now(), // latest click resets the 7-day window
    };
    await writeAttributionMap(map);

    // Report the click (server rate-limits to 10/IP/min). Fire-and-forget.
    api.clickPromotedProduct(apid);

    return map;
  },
);

export const hydrateAttribution = createAsyncThunk(
  'affiliate/hydrateAttribution',
  async () => {
    const map = await readAttributionMap();
    if (pruneExpired(map)) {
      await writeAttributionMap(map);
    }
    return map;
  },
);

export const updateAffiliate = createAsyncThunk(
  'affiliate/updateAffiliate',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.updateAffiliate(payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// --- SOCOLink dashboard (promoted products, orders, benefits, help) ---

export const getAffiliateInfo = createAsyncThunk(
  'SOCO_LINK/GET_AFFILIATE_INFO',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getAffiliateInfo();
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getAffiliatePromotedProducts = createAsyncThunk(
  'SOCO_LINK/GET_PROMOTED_PRODUCTS',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.getAffiliatePromotedProducts(params);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getAffiliateAllPromotedProducts = createAsyncThunk(
  'SOCO_LINK/GET_ALL_PROMOTED_PRODUCTS',
  async ({ params, fetchMore = false }, { rejectWithValue }) => {
    try {
      const response = await api.getAffiliatePromotedProducts(params);
      return { data: response.data.data || [], fetchMore };
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getAffiliatePromotedProductsCount = createAsyncThunk(
  'SOCO_LINK/GET_PROMOTED_PRODUCTS_COUNT',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getAffiliatePromotedProductsCount();
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getOrderSocoLinkList = createAsyncThunk(
  'SOCO_LINK/GET_ORDER_LIST',
  async ({ params, fetchMore = false }, { rejectWithValue }) => {
    try {
      const response = await api.getAffiliateOrderList(params);
      const data = response.data.data || [];
      return { data, fetchMore, hasMore: data.length >= (params.limit || 10) };
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getBenefitSocoLinkList = createAsyncThunk(
  'SOCO_LINK/GET_BENEFIT_LIST',
  async ({ params, fetchMore = false }, { rejectWithValue }) => {
    try {
      const response = await api.getAffiliateOrderList(params);
      const data = response.data.data || [];
      return { data, fetchMore, hasMore: data.length >= (params.limit || 10) };
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const searchPromotedProducts = createAsyncThunk(
  'SOCO_LINK/SEARCH_PROMOTED_PRODUCTS',
  async ({ params, fetchMore = false }, { rejectWithValue }) => {
    try {
      const response = await api.getAffiliatePromotedProducts(params);
      return { data: response.data.data || [], fetchMore };
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getPromotedProductsSearchCount = createAsyncThunk(
  'SOCO_LINK/GET_PROMOTED_PRODUCTS_SEARCH_COUNT',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.getAffiliatePromotedProductsCount(params);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getPromoteMoreProducts = createAsyncThunk(
  'SOCO_LINK/GET_PROMOTE_MORE_PRODUCTS',
  async ({ params, fetchMore = false }, { rejectWithValue }) => {
    try {
      const response = await Catalog.getProductsRequest(params);
      return { data: response.data.data || [], fetchMore };
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getPromoteMoreProductsCount = createAsyncThunk(
  'SOCO_LINK/GET_PROMOTE_MORE_PRODUCTS_COUNT',
  async (params, { rejectWithValue }) => {
    try {
      const response = await Catalog.getTotalProductsRequest(params);
      return response.data.data || 0;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const getSocoLinkHelpPosts = createAsyncThunk(
  'SOCO_LINK/GET_HELP_POSTS',
  async (_, { rejectWithValue }) => {
    try {
      const params = {
        filter: {
          'category.name': 'soco-link',
          post_type: 'help_page_article',
        },
      };
      const data = await HelpApi.getPosts(params);
      return data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);
